/**
 * §12's `RECEIVED:` block, as a control — the TD's statement of when a letter was served.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS FORM IS, AND WHAT IT IS NOT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It is not a field being set. Service is the moment a legal clock starts, and migration 033
 * makes every one of its properties structural: it cannot be recorded on a document that never
 * went out, it cannot be in the future, it cannot precede the issue, it must name a recipient,
 * and it is CORRECTABLE on the condition that the correction states its reason. Every one of
 * those is enforced at the database and answered by the route with a message written to be
 * shown; this form refuses the two it can refuse locally — an incomplete instant, and a
 * correction or withdrawal with no reason — so the TD is not sent to the server to be told
 * something the screen already knew.
 *
 * **Batch 2 never reaches this component.** §11's minutes are filed to the case, not served on
 * anybody; the panel draws {@link SERVICE_NOT_ON_MINUTES} in its place rather than a disabled
 * control, because a greyed-out button says "not yet" and the truth is "not ever".
 *
 * **No countdown is drawn here.** Recording service on the Hearing Letter starts nothing at
 * all (§2's second principle of natural justice is what it evidences), and the appeal window
 * that batch 3's service does start is drawn by {@link AppealClock}, from the shared
 * derivation, beside this form and never inside it.
 *
 * **A corrected service keeps its old time and its reason, and this is where they are read.**
 * The Technical Delegate's Decision #6 = A (19 Sep 2026; migration 066): every correction and
 * withdrawal is recorded with the instant and the name it replaced, the reason, who and when,
 * and the case file carries them on the document. They are listed at the foot of the block as
 * the letter's Service History, oldest first, and only when there is one - a letter nobody has
 * corrected draws nothing extra. The form's hints used to promise this history before anything
 * kept it; they now say where it is - and only when the case file came from an API that keeps
 * it ({@link ServiceCaptureProps.historyKept}); otherwise they promise nothing.
 *
 * **What is typed is checked the way the route checks it** (`recordTextFault`, one copy in
 * `@hockeytd/shared`): a recipient or a reason with no visible character is refused as missing,
 * and one carrying a bidi control, a control character other than TAB, LF and CR, a broken
 * character (a lone surrogate), or eight or more combining marks in a row is refused outright,
 * naming the character, before the route is troubled. The history draws every typed value, and the roster name, inside its own bidi isolate,
 * and each line paints only inside its own box.
 */

import { useEffect, useState } from "react";

import { recordTextFault, recordTextRefusal, type HearingDocumentState } from "@hockeytd/shared";

import type { HearingServiceCorrectionView, HearingServiceRequest } from "../../api/types.js";
import {
  SERVICE_BATCH_HEADING,
  SERVICE_BATCH_HINT,
  SERVICE_DISABLED_REASON,
  SERVICE_EXPLAINER,
  SERVICE_HISTORY_HEADING,
  SERVICE_LOCAL_REFUSAL,
  SERVICE_TIMEZONE_HINT,
  SERVICE_UNISSUED_HINT,
  serviceBeforeIssueRefusal,
  serviceHistoryLine,
  serviceInstantText,
  serviceWording,
  toOffsetInstant,
  zonedDateValue,
  zonedTimeValue,
} from "./serviceText.js";

export interface ServiceCaptureProps {
  /** Batch 1 or 3. The panel never renders this for batch 2. */
  batch: 1 | 3;
  state: HearingDocumentState;
  /** The recorded service instant, from the case record. Null when nothing is recorded. */
  servedAt: string | null;
  /**
   * When the document was issued, where that is known.
   *
   * Migration 033 refuses a service instant earlier than it, and that refusal is the one the
   * Technical Delegate met. Knowing the instant lets this form say what the earliest
   * permissible time is, and refuse the same thing a moment earlier — it admits nothing the
   * database would refuse.
   */
  issuedAt?: string | null;
  /**
   * Who signed for it, when it is known.
   *
   * The case-file projection carries `served_at` and not `served_on`, so this is null until
   * the panel has read `GET …/documents/:batch` — and a null here is rendered as "not read",
   * never as "nobody". {@link recipientKnown} is what tells the two apart.
   */
  servedOn: string | null;
  recipientKnown: boolean;
  /**
   * True when service on this document was corrected or withdrawn in this session, with the
   * instant it moved from. A corrected record must be capable of saying that it was corrected;
   * what it must never do is present the current statement as the original one.
   */
  correctedFrom: string | null;
  /**
   * The document's corrections and withdrawals, oldest first, as the case file carries them
   * (migration 066). Empty draws nothing. Unlike {@link correctedFrom} this is the RECORD, not
   * this session's memory of it: it survives a reload and another Technical Delegate's laptop.
   */
  corrections: readonly HearingServiceCorrectionView[];
  /**
   * True when the case file carried `serviceCorrections` for this letter - an API with migration
   * 066, which keeps every correction's reason and old time. False when it did not, and then no
   * sentence on this block promises that anything is kept (see `serviceWording`).
   */
  historyKept: boolean;
  open: boolean;
  onToggle: () => void;
  busy: boolean;
  onSubmit: (input: HearingServiceRequest) => void;
  timezone: string;
}

export function ServiceCapture({
  batch,
  state,
  servedAt,
  issuedAt = null,
  servedOn,
  recipientKnown,
  correctedFrom,
  corrections,
  historyKept,
  open,
  onToggle,
  busy,
  onSubmit,
  timezone,
}: ServiceCaptureProps): React.JSX.Element {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [recipient, setRecipient] = useState("");
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);

  const isCorrection = servedAt !== null;
  const wording = serviceWording(historyKept);

  /**
   * The route's rule for typed text, a moment earlier: the refusal to show, or null when the
   * statement is fine. `missing` is what an empty field is told, since a field of invisible
   * characters is an empty field.
   */
  const typedTextRefusal = (value: string, subject: string, missing: string): string | null => {
    const fault = recordTextFault(value);
    if (fault === null) return null;
    return fault.kind === "no_visible_text" ? missing : recordTextRefusal(subject, fault);
  };

  // Opening the form on a served document prefills it with what is recorded, so a correction
  // starts from the statement being corrected rather than from an empty box the TD retypes.
  useEffect(() => {
    if (!open) return;
    setRefusal(null);
    setReason("");
    setDate(servedAt === null ? "" : zonedDateValue(servedAt, timezone));
    setTime(servedAt === null ? "" : zonedTimeValue(servedAt, timezone));
    setRecipient(servedOn ?? "");
  }, [open, servedAt, servedOn, timezone]);

  /** Migration 033: a document that has not gone out has not been served. */
  const servable = state === "issued" || state === "filed";

  const record = (): void => {
    const instant = toOffsetInstant(date, time, timezone);
    if (instant === null) {
      setRefusal(SERVICE_LOCAL_REFUSAL.instant);
      return;
    }
    const recipientRefusal = typedTextRefusal(
      recipient,
      "Who it was served on",
      SERVICE_LOCAL_REFUSAL.servedOn,
    );
    if (recipientRefusal !== null) {
      setRefusal(recipientRefusal);
      return;
    }
    if (isCorrection) {
      const reasonRefusal = typedTextRefusal(reason, "The reason", wording.reasonRefusal);
      if (reasonRefusal !== null) {
        setRefusal(reasonRefusal);
        return;
      }
    }
    // Migration 033's rule, mirrored — see `serviceBeforeIssueRefusal`. The server still
    // decides; this only stops the TD being sent there to be told something the screen knew.
    if (issuedAt !== null && Date.parse(instant) < Date.parse(issuedAt)) {
      setRefusal(serviceBeforeIssueRefusal(issuedAt, timezone));
      return;
    }
    setRefusal(null);
    onSubmit({
      servedAt: instant,
      servedOn: recipient.trim(),
      correctionReason: isCorrection ? reason.trim() : null,
    });
  };

  const withdraw = (): void => {
    const reasonRefusal = typedTextRefusal(
      reason,
      "The reason",
      SERVICE_LOCAL_REFUSAL.withdrawReason,
    );
    if (reasonRefusal !== null) {
      setRefusal(reasonRefusal);
      return;
    }
    setRefusal(null);
    onSubmit({ servedAt: null, servedOn: null, correctionReason: reason.trim() });
  };

  return (
    <div className="svcblock" data-testid={`service-${String(batch)}`}>
      <span className="svcblock__head">{SERVICE_BATCH_HEADING[batch]}</span>

      {/* What service IS, said before anything is asked of the reader. */}
      <span className="svcblock__what" data-testid={`serviceExplainer-${String(batch)}`}>
        {SERVICE_EXPLAINER}
      </span>

      {servedAt === null ? (
        <span className="svcblock__state" data-testid={`serviceState-${String(batch)}`}>
          No service is recorded on this document.
        </span>
      ) : (
        <span className="svcblock__state" data-testid={`serviceState-${String(batch)}`}>
          <b>Served {serviceInstantText(servedAt, timezone)}</b>
          {recipientKnown && servedOn !== null && servedOn !== "" ? (
            <>
              {" "}
              on <bdi>{servedOn}</bdi>.
            </>
          ) : (
            <>. The recipient is recorded on the document and has not been read here.</>
          )}
        </span>
      )}

      {correctedFrom === null ? null : (
        <span className="svcblock__corrected" data-testid={`serviceCorrected-${String(batch)}`}>
          Corrected in this session — it previously read{" "}
          {serviceInstantText(correctedFrom, timezone)}.
          {wording.correctedNote === "" ? null : <> {wording.correctedNote}</>}
        </span>
      )}

      {!servable ? (
        /*
         * A DISABLED CONTROL WITH THE REASON ON IT, not an absent one.
         *
         * The screen used to draw a sentence where the button would be. A reader looking for
         * the control read the sentence as a caption on a control that was missing, and there
         * was nothing to press that could tell him why. A greyed-out button with the reason
         * beside it says the same thing at the place he is looking.
         */
        <>
          <button
            type="button"
            className="gbtn gbtn--sm"
            disabled
            title={SERVICE_DISABLED_REASON}
            data-testid={`serviceDisabled-${String(batch)}`}
          >
            Record Service
          </button>
          <span className="svcblock__hint">
            {SERVICE_DISABLED_REASON} {SERVICE_UNISSUED_HINT}
          </span>
        </>
      ) : !open ? (
        <button
          type="button"
          className="gbtn gbtn--sm"
          disabled={busy}
          onClick={onToggle}
          data-testid={`serviceOpen-${String(batch)}`}
        >
          {isCorrection ? "Correct Service" : "Record Service"}
        </button>
      ) : (
        <div className="svcform" data-testid={`serviceForm-${String(batch)}`}>
          <span className="svcform__hint">{SERVICE_BATCH_HINT[batch]}</span>
          {issuedAt === null ? null : (
            <span className="svcform__hint" data-testid={`serviceIssuedAt-${String(batch)}`}>
              This letter was issued {serviceInstantText(issuedAt, timezone)}. Service cannot be
              earlier than that.
            </span>
          )}

          <div className="svcform__row">
            <label className="svcform__field">
              <span className="field__label">Date Of Service</span>
              <input
                type="date"
                aria-label={`Batch ${String(batch)} date of service`}
                value={date}
                onChange={(event) => {
                  setDate(event.target.value);
                }}
              />
            </label>
            <label className="svcform__field">
              <span className="field__label">Time Of Service</span>
              <input
                type="time"
                aria-label={`Batch ${String(batch)} time of service`}
                value={time}
                onChange={(event) => {
                  setTime(event.target.value);
                }}
              />
            </label>
          </div>
          <span className="svcform__hint">{SERVICE_TIMEZONE_HINT}</span>

          <label className="svcform__field">
            <span className="field__label">Served On</span>
            <input
              type="text"
              aria-label={`Batch ${String(batch)} served on`}
              placeholder="Who signed for it"
              value={recipient}
              onChange={(event) => {
                setRecipient(event.target.value);
              }}
            />
          </label>

          {isCorrection ? (
            <>
              <label className="svcform__field">
                <span className="field__label">Reason</span>
                <textarea
                  rows={2}
                  aria-label={`Batch ${String(batch)} reason for the correction`}
                  placeholder="Why the recorded service is being changed or withdrawn"
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                />
              </label>
              {/* Said at the field, before a word of it is typed: the record keeps what is. */}
              <span className="svcform__hint" data-testid={`serviceReasonPrivacy-${String(batch)}`}>
                {wording.reasonPrivacyHint}
              </span>
              <span className="svcform__hint" data-testid={`serviceCorrectionHint-${String(batch)}`}>
                {wording.correctionHint}
              </span>
            </>
          ) : null}

          {refusal === null ? null : (
            <span
              className="svcform__refusal"
              role="alert"
              data-testid={`serviceRefusal-${String(batch)}`}
            >
              {refusal}
            </span>
          )}

          <div className="svcform__acts">
            <button
              type="button"
              className="gbtn gbtn--solid gbtn--sm"
              disabled={busy}
              onClick={record}
              data-testid={`serviceSubmit-${String(batch)}`}
            >
              {busy ? "Working…" : isCorrection ? "Save Correction" : "Record Service"}
            </button>
            {isCorrection ? (
              <button
                type="button"
                className="gbtn gbtn--sm"
                disabled={busy}
                onClick={withdraw}
                data-testid={`serviceWithdraw-${String(batch)}`}
              >
                Withdraw Service
              </button>
            ) : null}
            <button type="button" className="gbtn gbtn--sm" onClick={onToggle}>
              Cancel
            </button>
          </div>

          {isCorrection ? (
            <span className="svcform__hint" data-testid={`serviceWithdrawHint-${String(batch)}`}>
              {wording.withdrawHint}
            </span>
          ) : null}
        </div>
      )}

      {/*
        * THE SERVICE HISTORY (migration 066, Decision #6 = A) - under the control, where a
        * reader who has just corrected the time looks for what it used to say. An ORDERED list,
        * because the order is the history: each line moved from where the line above it left the
        * service. Drawn from the case record, so it is the same on every laptop and after a
        * reload, which the session's own "Corrected in this session" line above is not.
        *
        * EVERY TYPED VALUE IN ITS OWN BIDI ISOLATE (fix round 1, security L2). The recipients
        * and the reason are what somebody typed, and the media name comes from the roster; each
        * sits in a <bdi>, and the three parts of the line are isolated from one another by the
        * stylesheet, so a right-to-left name, or a stray control that reached the record before
        * the route refused them, can reorder nothing but itself.
        */}
      {corrections.length === 0 ? null : (
        <div className="svchistory" data-testid={`serviceHistory-${String(batch)}`}>
          <span className="svchistory__head">{SERVICE_HISTORY_HEADING}</span>
          <ol className="svchistory__list">
            {corrections.map((correction, index) => {
              const line = serviceHistoryLine(correction, timezone);
              return (
                <li
                  key={`${correction.at}-${String(index)}`}
                  className="svchistory__item"
                  data-testid={`serviceHistoryItem-${String(batch)}`}
                >
                  {/* The line's own block, which paints only inside itself - on the <li> the
                      containment clipped the list's number too (final round, backend G1). */}
                  <span className="svchistory__line">
                    <span className="svchistory__what">
                      {line.toAt === null ? (
                        <>
                          Withdrawn: it read {line.fromAt} (on <bdi>{line.fromOn}</bdi>)
                        </>
                      ) : (
                        <>
                          Moved from {line.fromAt} (on <bdi>{line.fromOn}</bdi>) to {line.toAt} (on{" "}
                          <bdi>{line.toOn}</bdi>)
                        </>
                      )}
                    </span>{" "}
                    &mdash; <bdi className="svchistory__reason">{line.reason}</bdi> &mdash;{" "}
                    <span className="svchistory__who">
                      <bdi>{line.who}</bdi>, {line.when}
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
